import * as Sentry from '@sentry/vue';

import { SENSITIVE_QUERY_KEYS, sanitizeSensitiveQuery } from '@/wabunka-src/common/utils/url/sanitizeSensitiveQuery';

import type { Breadcrumb, ErrorEvent, Event } from '@sentry/vue';

type SentryTags = 'wabunka';

/**
 * Replay の記録を行わないパス。
 *
 * rrweb の Meta イベントはドキュメント URL をそのまま記録し、Sentry 側のフックからは書き換えられない
 * （beforeAddRecordingEvent は Custom イベントにしか適用されない）。URL そのものが他人の購読を停止できる
 * ケイパビリティになるページでは、記録を止めるほかに塞ぐ手段がない。
 */
const REPLAY_BLOCKED_PATHS = ['/waitlist/unsubscribe'];

export const isReplayBlockedPath = (path: string): boolean => REPLAY_BLOCKED_PATHS.includes(path === '/' ? path : path.replace(/\/$/, ''));

/**
 * span 属性から落とすキー。
 *
 * `browserTracingIntegration({ router })` が使う @sentry/vue の `instrumentVueRouter` は、
 * 遷移先の query と params を pageload / navigation span の属性へ 1 つずつ展開する。
 * URL 文字列ではなく属性として載るため、`request.url` の除去では落ちない。
 */
const SENSITIVE_SPAN_ATTRIBUTE_KEYS = new Set(['query.', 'params.', 'url.path.parameter.'].flatMap((prefix) => SENSITIVE_QUERY_KEYS.map((key) => `${prefix}${key}`)));

const withoutSensitiveAttributes = <T extends Record<string, unknown>>(data: T): T => Object.fromEntries(Object.entries(data).filter(([key]) => !SENSITIVE_SPAN_ATTRIBUTE_KEYS.has(key))) as T;

/**
 * イベントに載る秘匿クエリを落とす。
 *
 * event processor は error / transaction / replay_event のすべてが通るため、種別ごとのフックに
 * 同じ除去を書き分けずに済む。とくに replay_event は beforeSend を通らず、現在 URL を
 * `request.url` ではなく `urls` に積むため、両方を見ないと素通しになる。
 */
export const scrubSensitiveUrls = (event: Event): Event => {
  if (event.request?.url) {
    event.request.url = sanitizeSensitiveQuery(event.request.url);
  }
  // httpContextIntegration が document.referrer を Referer ヘッダとして載せる。
  // 解除ページの referrer: no-referrer だけに頼ると、meta が外れた瞬間に素通しになる
  const headers = event.request?.headers;
  if (headers && typeof headers.Referer === 'string') {
    headers.Referer = sanitizeSensitiveQuery(headers.Referer);
  }
  // urls は ReplayEvent にしか無く、基底の Event 型には現れない
  const { urls } = event as Event & { urls?: string[] };
  if (Array.isArray(urls)) {
    (event as Event & { urls?: string[] }).urls = urls.map(sanitizeSensitiveQuery);
  }
  if (event.contexts?.trace?.data) {
    event.contexts.trace.data = withoutSensitiveAttributes(event.contexts.trace.data);
  }
  event.spans?.forEach((span) => {
    span.data = withoutSensitiveAttributes(span.data);
    // browser.DNS / browser.request などの計測 span は PerformanceNavigationTiming の
    // name をそのまま description に置く。これはドキュメント URL 全体で、クエリを含む
    if (span.description) {
      span.description = sanitizeSensitiveQuery(span.description);
    }
  });
  return event;
};

/** breadcrumb は event processor を通らないため、遷移先 URL はここで落とす */
export const scrubBreadcrumbUrls = (breadcrumb: Breadcrumb): Breadcrumb => {
  if (typeof breadcrumb.data?.from === 'string') {
    breadcrumb.data.from = sanitizeSensitiveQuery(breadcrumb.data.from);
  }
  if (typeof breadcrumb.data?.to === 'string') {
    breadcrumb.data.to = sanitizeSensitiveQuery(breadcrumb.data.to);
  }
  return breadcrumb;
};

/** ユーザーのネットワーク起因エラーは Sentry に送信しない */
export const dropNetworkErrors = (event: ErrorEvent, hint?: { originalException?: unknown }): ErrorEvent | null => {
  const error = hint?.originalException;
  if (error instanceof Error) {
    // 例: microCMS・外部 API へのリクエストがオフライン等で失敗したケース
    const isNetworkError =
      error.message.includes('<no response>') ||
      // microCMS SDK のネットワークエラー: "Network Error.\n  Details: TypeError: Failed to fetch"
      error.message.includes('Details: TypeError: Failed to fetch') ||
      error.message.includes('TypeError: Load failed') ||
      // Algolia 検索クライアントが全リトライ対象ホストに到達できなかった場合の RetryError。
      // 不安定な回線で発生する一時的なホスト不達で、App ID 誤設定なら全ユーザーで多発するためアプリ起因ではない。
      // vue-instantsearch 内部の Promise reject が unhandledrejection として届くため beforeSend で除外する。
      error.message.includes('Unreachable hosts');
    if (isNetworkError) return null;
  }
  return event;
};

/**
 * Sentry にエラーを送信する
 */
export const captureException = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  e: any,
  tag?: SentryTags,
) => {
  Sentry.captureException(e, tag ? { tags: { from: tag } } : undefined);
};

/**
 * Sentry に warning レベルのメッセージを送信する
 */
export const captureWarning = (message: string, tag?: SentryTags, extra?: Record<string, unknown>) => {
  Sentry.captureMessage(message, {
    level: 'warning',
    ...(tag ? { tags: { from: tag } } : {}),
    ...(extra ? { extra } : {}),
  });
};

/**
 * create Error and send to sentry
 */
export const createAndCaptureError = (params: Parameters<typeof createError>[0] & { customMessage?: string }, tag?: SentryTags) => {
  const err = createError(params);
  // throwing createError obj to sentry would cause 429 due to its size
  const reportingError = new Error(`createError: ${params.customMessage ?? ''} ${JSON.stringify(params)}`);
  captureException(reportingError, tag);
  return err;
};
